// Карточки новостей по Figma («Main Article» и «Small News Card»). image_url — наш прокси, адрес как есть.
// Язык статьи может не совпадать с языком интерфейса — lang на заголовке.
import { useRef, useState } from 'react';
import type { SponsoredPlacement } from '../analytics/events';
import { sponsoredClick, useSponsoredView } from '../analytics/sponsored';
import { useNavigate } from 'react-router-dom';
import type { NewsAd, NewsCard } from '../api/leagueTypes';
import { timeAgo } from '../i18n/timeAgo';
import { openExternal } from '../telegram/webapp';
import { withAds } from './newsAds';
import styles from './NewsCardView.module.css';

/** Картинка статьи; не загрузилась (прокси не достал источник) — ровная серая заглушка вместо значка битой картинки. */
function NewsImage({ src, className }: { src: string; className?: string }) {
  const [failed, setFailed] = useState(false);
  if (failed) return <span className={className} aria-hidden="true" />;
  return <img className={className} src={src} alt="" loading="lazy" onError={() => setFailed(true)} />;
}

/** «источник • время»: сокращается многоточием только источник; время целиком, не влезает и при одном символе
 *  источника — переносится строкой ниже (HQ-1175 v2: на 360 px и в ta обрезалось «5 minut…»). */
function SourceLine({ article }: { article: NewsCard }) {
  const time = timeAgo(article.published_at);
  const source = article.source_name;
  return (
    <span className={styles.sourceLine}>
      {source && <span className={styles.source}>{source}</span>}
      {source && time && <span className={styles.dot} aria-hidden="true">•</span>}
      {time && <span className={styles.time}>{time}</span>}
    </span>
  );
}

/** Крупная карточка: картинка во всю ширину, заголовок, «источник • время» («2 hours ago», старше недели — дата). Без картинки — только текст (так в Figma). */
export function LeadNewsCard({ article }: { article: NewsCard }) {
  const navigate = useNavigate();
  return (
    <button className={styles.lead} onClick={() => navigate(`/news/${article.id}`)}>
      {article.image_url && <NewsImage className={styles.leadImage} src={article.image_url} />}
      <span className={styles.title} lang={article.language}>{article.title}</span>
      <SourceLine article={article} />
    </button>
  );
}

/** Строка: миниатюра слева, заголовок сверху и «источник • время» внизу справа. */
export function NewsCardView({ article }: { article: NewsCard }) {
  const navigate = useNavigate();
  const image = article.image_url;
  if (!image) return <LeadNewsCard article={article} />;
  return (
    <button className={styles.row} onClick={() => navigate(`/news/${article.id}`)}>
      <NewsImage className={styles.thumb} src={image} />
      <span className={styles.rowText}>
        <span className={`${styles.title} ${styles.clamp}`} lang={article.language}>{article.title}</span>
        <SourceLine article={article} />
      </span>
    </button>
  );
}

/** Блок секции: вид каждой карточки — серверный card (main/compact); в ответе без card — первая крупно, остальные
 *  строками (Figma, как в iOS NewsArticlesBlock). place — место карточек спонсоров для аналитики (рекламу несёт хаб). */
export function NewsArticlesBlock({ articles, ads, place = 'news_hub' }: { articles: NewsCard[]; ads?: NewsAd[]; place?: SponsoredPlacement }) {
  if (!articles.length) return null;
  const kinds = new Map(cardKinds(articles).map((k, i) => [articles[i]!.id, k]));
  return (
    <div className={styles.block}>
      {withAds(articles, ads).map((e) => e.kind === 'ad'
        ? <AdNewsCard key={e.ad.id} ad={e.ad} place={place} />
        : kinds.get(e.article.id) === 'main'
          ? <LeadNewsCard key={e.article.id} article={e.article} />
          : <NewsCardView key={e.article.id} article={e.article} />)}
    </div>
  );
}

/** Вид карточек секции: серверный card; в ответе без card — первая main, остальные compact. */
export function cardKinds(articles: NewsCard[]): Array<'main' | 'compact'> {
  const byServer = articles.some((a) => a.card);
  return articles.map((a, i) => ((byServer ? a.card !== 'compact' : i === 0) ? 'main' : 'compact'));
}

/** Рекламная карточка (гид §2.9): вид статьи по card, всегда с пометкой label и именем advertiser; нажатие — link_url
 *  во внешнем браузере (это не статья, news/{id} не зовём). Без картинки сервер шлёт main.
 *  place — экран (news_hub / news_feed / matches): sponsored_view по видимости, sponsored_click до открытия ссылки. */
export function AdNewsCard({ ad, place }: { ad: NewsAd; place: SponsoredPlacement }) {
  const ref = useRef<HTMLButtonElement>(null);
  useSponsoredView(ref, ad, place);
  const meta = (
    <span className={styles.meta}>
      <span className={styles.adLabel}>{ad.label}</span> {ad.advertiser}
    </span>
  );
  const open = () => { sponsoredClick(ad, place); openExternal(ad.link_url); };
  if (ad.card === 'compact' && ad.image_url) {
    return (
      <button ref={ref} className={styles.row} onClick={open}>
        <NewsImage className={styles.thumb} src={ad.image_url} />
        <span className={styles.rowText}>
          <span className={`${styles.title} ${styles.clamp}`}>{ad.title}</span>
          {meta}
        </span>
      </button>
    );
  }
  return (
    <button ref={ref} className={styles.lead} onClick={open}>
      {ad.image_url && <NewsImage className={styles.leadImage} src={ad.image_url} />}
      <span className={styles.title}>{ad.title}</span>
      {ad.summary && <span className={styles.adSummary}>{ad.summary}</span>}
      {meta}
      {ad.cta?.label && <span className={styles.adCta}>{ad.cta.label}</span>}
    </button>
  );
}
